import React, { memo, useMemo, useEffect, useCallback } from 'react';
import { Proposal } from '../types';
import { BlockContainer } from '../BlockContainer';
import classNames from 'classnames';
import { formGroup, formArray, formControl } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/proposal/hooks';
import { useProposalContext, useProposalDataContext, ProposalFormData, defineExtraSettingsForm } from '@modules/proposal/ProposalBuilderPage';
import { R } from '@core/utils/r';
import { SectionItem, PricingHeader, PricingFooter, LineItem } from './LineItem';
import {
  defineProposalLineItemForm,
  openProposalProductsOverlay,
  defineProposalPriceSectionForm,
  defineTMFeeForm,
  defineProposalDiscountForm,
  TmFeeFormData,
} from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { Container } from 'reactstrap';
import { useDidMount } from '@core/utils/hooks/react';
import { Config } from '@config';
import { DealIntervalResponse } from '@business/entities/deal';
import { CommonService, ProposalService } from '@business/services';
import { useAccountSettings } from '@core/utils/hooks/misc/useSettings';
import { LaborLineItem } from './LaborLineItem';
import { FeeLineItem } from './FeeLineItem';
import { TableColumn } from '@modules/common/TableColumn';
import { Formatter } from '@core/utils/formatter';
import { ProposalDiscountCategory, ProposalStatus } from '@business/entities/proposal';
import { TMFeeType } from '@business/entities/tm';
import { useCompany } from '@core/utils/hooks/misc';
import './index.scss';
import { FormCheckbox } from '@core/components/form';
import { defineTmInvoiceSalesTaxForm } from '@modules/common/TmInvoiceSalesTax';

export const FeeHeader = memo((props: { primaryColor?: string; editable?: boolean }) => {
  const { primaryColor, editable = false } = props;
  return (
    <div className="proposal-table-header" style={{ color: primaryColor }}>
      <TableColumn width={50} maxWidth={50}></TableColumn>
      <TableColumn>Fees</TableColumn>
      {!!editable && <TableColumn maxWidth={100}>%</TableColumn>}
      <TableColumn maxWidth={200}>Total</TableColumn>
      <TableColumn maxWidth={5}></TableColumn>
    </div>
  );
});

function getDummyProduct(description: string, qty: number, rate: number, service = '') {
  const linItem = {
    labourTypeId: '',
    hour: 0,
    cost: 0,
    price: 0,
  };
  return {
    isServicePhase: false,
    service,
    description,
    qty,
    rate,
    title: '',
    classId: '',
    salesReps: [],
    taxable: false,
    isOptional: false,
    frequency: DealIntervalResponse.onetime,
    flexibleQty: false,
    selected: true,
    sectionId: undefined,
    id: R.random(1, 9999),
    labour: undefined,
    labourType: undefined,
    labourPrice: undefined,
    labourCost: undefined,
    labourItems: [linItem],
    productLabourItems: [linItem],
    cost: 0,
    packageItems: [],
    productImage: '',
    isDiscount: false,
    tierId: '',
    unitOfMeasure: '',
  };
}

export enum PricingBlockVariation {
  pricing = 'pricing',
}

export const PricingBlock = memo((props: Proposal.BlockProps<PricingBlockVariation>) => {
  const { variation, preview, editable } = props;
  const proposal = useProposalContext();
  const { form: proposalForm } = useProposalContext();
  const proposalFormValues = useNonNilObservable(proposalForm.value$);
  const combineLabor = useMemo(() => proposalFormValues.combineLabor, [proposalFormValues]);

  const bloc = proposal?.bloc;
  const form = useMemo(() => {
    if (proposal) return proposal.form;
    return formGroup({
      products: formArray(() => defineProposalLineItemForm()),
      sections: formArray(() => defineProposalPriceSectionForm()),
      productsAdvance: defineBlockAdvanceForm(),
      fees: formArray(() => defineTMFeeForm()),
      discount: defineProposalDiscountForm(),
      extraSettings: defineExtraSettingsForm(),
      salesTax: defineTmInvoiceSalesTaxForm(),
    });
  }, [proposal]);

  const taxAfterDiscount = useMemo(() => proposal?.taxAfterDiscount, [proposal]);

  const accountSettings = useAccountSettings();
  const isLaborActive = useMemo(() => !!accountSettings.timeMaterials, [accountSettings.timeMaterials]);
  //   const isLaborActive = useMemo(() => CommonService.isHive(), []);
  const labourTypes = useObservable(bloc?.labourTypes$, []);
  const formValue = useNonNilObservable(form.value$) as ProposalFormData;
  const lineItemForms = useObservable(form.controls.products.items$, []);
  const lineItemsValues = useObservable(form.controls.products.value$);
  const SectionForms = useObservable(form.controls.sections.items$, []);
  const primaryColor = useMemo(() => proposal?.form.value.primaryColor, [proposal]);
  const proposalData = useProposalDataContext();
  const formProducts = useNonNilObservable(form.controls.products.value$);
  const discountFormValue = useNonNilObservable(form.controls.discount.value$);
  const feeFormValues = useNonNilObservable(form.controls.fees.value$);
  const noSectionItems = useMemo(() => lineItemForms.filter(item => R.toNumber(item.controls.sectionId.value) == -1), [lineItemForms]);
  const company = useCompany();
  const signee = useMemo(() => {
    if (proposalData?.signee) {
      return proposalData.signee;
    } else {
      return null;
    }
  }, [proposalData]);
  const canSign = useMemo(() => signee && signee.can_sign && !signee.is_signed, [signee]) ? false : true;
  useDidMount(() => {
    if (Config.IS_ADMIN) {
      form.patchValue({
        products: [getDummyProduct('Your Product/Service Here', 1, 100), getDummyProduct('Your Product/Service Here', 1, 200)],
      });
    }
  });

  const onlyDisplayTotal = useMemo(() => formValue.extraSettings.onlyDisplayTotal, [formValue.extraSettings.onlyDisplayTotal]);
  const showOnlyTotal = useMemo(() => !!onlyDisplayTotal && !!!editable && !!preview, [editable, onlyDisplayTotal, preview]);

  const laborCost = useMemo(() => {
    if (lineItemsValues && lineItemsValues.length > 0) {
      const laborItemsLaborCost = ProposalService.getAllLineItems(lineItemsValues);
      const totalLaborCost = R.toNumber(laborItemsLaborCost) as number;
      return totalLaborCost;
    }
    return 0;
  }, [lineItemsValues]);

  //   const total = R.isNumber(laborTotal) ? laborTotal + subtotal : subtotal;
  const total = useMemo(() => {
    const total = ProposalService.getGrandTotal(formValue.products);
    const percentTotal = ProposalService.getFeeTotal(formValue.fees);
    const feeTotal = ProposalService.getFeeTotalAmount(formValue.fees, formValue.products);
    // if (!editable && laborCost > 0) {
    //   const tempTotal = total + laborCost;
    //   return (tempTotal / 100) * percentTotal + tempTotal;
    // }
    return feeTotal + total;
  }, [formValue.fees, formValue.products]);

  const totalWithoutFees = useMemo(() => {
    const total = ProposalService.getGrandTotal(formValue.products, editable);
    if (!editable && laborCost > 0) {
      return total + laborCost;
    }
    return total;
  }, [editable, formValue.products, laborCost]);

  const proposalStatus = useMemo(() => proposalData?.status, [proposalData]);
  const canCheckProducts = useMemo(() => {
    if (!proposalStatus) return true;
    return proposalStatus !== ProposalStatus.accepted;
  }, [proposalStatus]);

  useConfigureBlock({
    props,
    onEdit: () =>
      openProposalProductsOverlay({
        initialItems: Config.IS_ADMIN ? [] : formValue.products,
        initialSections: Config.IS_ADMIN ? [] : formValue.sections,
        initialFees: Config.IS_ADMIN ? [] : formValue.fees,
        advanceForm: formValue.productsAdvance,
        initialExtraSettings: formValue.extraSettings,
        onSubmit: (products, sections, productsAdvance, fees, extraSettings) => {
          if (Config.IS_ADMIN) {
            form.patchValue({ productsAdvance });
          } else {
            form.patchValue({ products, sections, productsAdvance, fees, extraSettings });
          }
        },
      }),
  });

  // const getFeeTotal = (percent: number, total: number) => {
  //   return (total * percent) / 100;
  // };
  const getFeeTotal = useCallback(
    (item: TmFeeFormData) => {
      const feeType = item.feeType == TMFeeType.item ? false : true;
      const t = ProposalService.getGrandTotal(formValue.products, feeType);
      return (t * item.percent) / 100;
    },
    [formValue.products],
  );
  //   useEffect(() => {
  //     if (lineItemsValues && lineItemsValues.length > 0 && !editable) {
  //       const laborItemsLaborCost = ProposalService.getAllLineItems(lineItemsValues);
  //       const totalLaborCost = R.toNumber(laborItemsLaborCost) as number;
  //       const hasLaborItem = lineItemsValues.find(item => item.service === 'totalLaborCost');
  //       if (totalLaborCost > 0 && !hasLaborItem) {
  //         const control = form.controls.products.push();
  //         control.setValue(getDummyProduct('Labor Cost', 1, R.toNumber(laborItemsLaborCost) as number, 'totalLaborCost'));
  //       }
  //     }
  //   }, [editable, form.controls.products, laborTotal, lineItemsValues]);
  const fees = useMemo(() => formValue.fees.filter(f => f.isChecked), [formValue.fees]);

  useEffect(() => {
    if (!!lineItemForms.length && !!!SectionForms.length) {
      form.patchValue({ sections: [{ title: 'Default Section', checked: true, id: 0, menuOrder: 0 }] });
    }
  }, [SectionForms.length, form, lineItemForms, lineItemForms.length, lineItemsValues]);

  const isDiscountByLabor = useMemo(() => formValue.discount.category == ProposalDiscountCategory.labor, [formValue.discount.category]);
  const isDiscountByItems = useMemo(() => formValue.discount.category == ProposalDiscountCategory.items, [formValue.discount.category]);

  const taxableFeeTotal = useMemo(() => {
    const taxableFees = fees.filter(f => f.taxable);
    return taxableFees.reduce((acc, fee) => acc + getFeeTotal(fee), 0);
  }, [fees, getFeeTotal]);

  const salesTax = useMemo(() => (proposal ? proposal.form.value.salesTax.value : 0), [proposal]);

  const salesTaxAmount = useMemo(() => {
    if (R.toNumber(salesTax) == 0 || R.isNaN(R.toNumber(salesTax))) {
      return 0;
    }
    if (taxAfterDiscount) {
      const tax = ProposalService.getTaxAmt(form.controls.products.value, formValue.salesTax, formValue.discount, formValue.fees);
      form.patchValue({ salesTax: { ...formValue.salesTax, taxAmount: tax } });
      return tax;
    }
    return ProposalService.calculateTotalSalesTax(R.cloneDeep(formValue.products), salesTax || 0, formValue.fees);
    return ((totalWithoutFees + taxableFeeTotal) * R.toNumber(salesTax)) / 100;
  }, [
    form,
    formValue.discount,
    formValue.fees,
    formValue.products,
    formValue.salesTax,
    salesTax,
    taxAfterDiscount,
    taxableFeeTotal,
    totalWithoutFees,
  ]);

  const discountAmount = useMemo(() => {
    const isDiscount = formValue.discount.isDiscount;
    if (isDiscount) {
      const discountType = formValue.discount.type;
      const discountValue = formValue.discount.value;
      if (discountType === 'fixed') {
        return R.toNumber(discountValue);
      } else {
        // filter the formProducts which has isDiscount true and filter packageItems array inside the formProducts object which has isDiscount true
        const filteredData = ProposalService.discountableRows(formProducts);

        if (isDiscountByLabor) {
          return R.toNumber(ProposalService.getProposalTotalLaborPrice(filteredData, formValue.discount));
          //   const laborTotal = ProposalService.getProposalTotalLaborPrice(formProducts);
          //   return R.toNumber(ProposalService.getDiscountAmount(discountFormValue, laborTotal));
        }

        if (isDiscountByItems) {
          return R.toNumber(ProposalService.getProposalTotalPartsPrice(filteredData, formValue.discount));
          //   const itemsTotal = ProposalService.getProposalTotalPartsPrice(formProducts);
          //   return R.toNumber(ProposalService.getDiscountAmount(discountFormValue, itemsTotal));
        }
        return ProposalService.calculateGrandTotalDiscountEditable(formValue.products, editable, formValue.discount);
        // const ProposalItemsTotal = ProposalService.calculateGrandTotalDiscount(formValue.products, editable, formValue.discount);
        // const percentTotal = ProposalService.getFeeTotal(feeFormValues);
        // const gTotal = ProposalItemsTotal + (ProposalItemsTotal / 100) * percentTotal;
        // return R.toNumber((gTotal * discountValue) / 100);

        // return R.toNumber((total * discountValue) / 100);
      }
    }
    return 0;
  }, [editable, formProducts, formValue.discount, formValue.products, isDiscountByItems, isDiscountByLabor]);

  const footerGrandTotal = useMemo(() => {
    if (salesTaxAmount > 0) {
      return total - discountAmount + salesTaxAmount;
    }
    return total - discountAmount;
  }, [discountAmount, salesTaxAmount, total]);

  const showLaborCostLineItem = useMemo(() => isLaborActive && !combineLabor, [isLaborActive, combineLabor]);

  return (
    <BlockContainer advanceForm={formValue.productsAdvance} className={classNames('d-flex flex-fill', 'block-pricing', variation)}>
      <Container>
        {!SectionForms.length && <PricingHeader isLaborActive={isLaborActive} primaryColor={primaryColor} />}
        {SectionForms.map((item, index) => (
          <SectionItem
            key={item.uid}
            form={item}
            secIndex={index}
            lineItemForms={lineItemForms}
            primaryColor={primaryColor}
            total={total}
            canSign={canSign}
            labourTypes={labourTypes}
            isLaborActive={isLaborActive}
            editable={editable}
            canCheckProducts={canCheckProducts}
          />
        ))}
        {noSectionItems.length > 0 && (
          <div className="mt-5">
            <PricingHeader isLaborActive={isLaborActive} primaryColor={primaryColor} editable={editable} />
            {noSectionItems.map((item, index) => {
              return (
                <>
                  <LineItem
                    key={item.uid}
                    isLaborActive={isLaborActive}
                    form={item}
                    index={index}
                    canSign={canSign}
                    labourTypes={labourTypes}
                    isPackage={item.value.packageItems.length > 0 ? true : false}
                    editable={editable}
                    canCheckProducts={canCheckProducts}
                  />
                  {item.controls.packageItems.items.map((packageItem, pacInd) => {
                    return (
                      <div className="ml-5" key={packageItem.uid}>
                        <LineItem
                          key={packageItem.uid}
                          isLaborActive={isLaborActive}
                          form={packageItem}
                          index={pacInd}
                          canSign={canSign}
                          labourTypes={labourTypes}
                          isPackage={item.value.packageItems.length > 0 ? true : false}
                          editable={editable}
                          canCheckProducts={canCheckProducts}
                        />
                      </div>
                    );
                  })}
                </>
              );
            })}
          </div>
        )}

        {showLaborCostLineItem && <LaborLineItem editable={editable} laborCost={laborCost} title="Labor Cost" />}
        {!!fees.length && (
          <div className="mt-5">
            <FeeHeader primaryColor={primaryColor} editable={editable} />
          </div>
        )}

        {!!fees.length &&
          fees.map((f, index) => {
            return <FeeLineItem key={index} editable={editable} serviceName={f.service} percent={f.percent} total={getFeeTotal(f)} title={f.name} />;
          })}

        {salesTaxAmount > 0 && (
          <div className="d-flex align-items-center" style={{ padding: '10px 0px', borderBottom: '2px solid #e0e0e0' }}>
            <TableColumn width={50} maxWidth={50}></TableColumn>
            <TableColumn>Sales Tax</TableColumn>
            <TableColumn maxWidth={100}></TableColumn>
            <TableColumn style={{ marginLeft: 20 }} maxWidth={200}>
              {Formatter.formatCurrency(salesTaxAmount, company.currencyCode)}
            </TableColumn>
            <TableColumn maxWidth={5}></TableColumn>
          </div>
        )}

        {discountAmount != 0 && (
          <div className="d-flex align-items-center" style={{ padding: '10px 0px', borderBottom: '2px solid #e0e0e0' }}>
            <TableColumn width={50} maxWidth={50}></TableColumn>
            <TableColumn>Discount</TableColumn>
            <TableColumn maxWidth={100}></TableColumn>
            <TableColumn style={{ marginLeft: 20 }} maxWidth={200}>
              -{Formatter.formatCurrency(discountAmount, company.currencyCode)}
            </TableColumn>
            <TableColumn maxWidth={5}></TableColumn>
          </div>
        )}
        {(!!SectionForms.length || !!noSectionItems.length) && <PricingFooter total={footerGrandTotal} primaryColor={primaryColor} />}
      </Container>
    </BlockContainer>
  );
});
